import * as Enumerable from 'linq';
import { AccountingSystemMapping, CustomerMapping, SupplierMapping, TaxRateMapping, TechnicianMapping } from '../entities/accounting/accounting.system.mapping';
import { Entity } from '../entities/entity';
import { InvoiceContact, InvoiceContactType } from '../entities/invoicing/invoicing.contact';
import { QuickbookTaxInclusiveOption } from '../entities/purchase/purchase.order';
import { TaxInclusiveOption } from '../entities/purchase/purchase.order.line';

export class AccountingSystemMappingUtility {

    public static getInvoiceGL(mapping: AccountingSystemMapping): Entity {
        if (!mapping || !mapping.invoiceGL) return this.getNotMapped();
        return mapping.invoiceGL;
    }

    public static getItemRef(mapping: AccountingSystemMapping): Entity {
        if (!mapping || !mapping.item) return this.getNotMapped();
        return mapping.item;
    }

    public static getBillGL(mapping: AccountingSystemMapping): Entity {
        if (!mapping || !mapping.billGL) return this.getNotMapped();
        return mapping.billGL;
    }

    public static getPurchaseGL(mapping: AccountingSystemMapping): Entity {
        if (!mapping || !mapping.purchaseGL)
            return this.getNotMapped();

        return mapping.purchaseGL;
    }

    public static getQuickbookTaxInclusiveOption(option: TaxInclusiveOption): QuickbookTaxInclusiveOption {

        switch (option) {
            case TaxInclusiveOption.Exclusive:
                return QuickbookTaxInclusiveOption.Exclusive;
            case TaxInclusiveOption.Inclusive:
                return QuickbookTaxInclusiveOption.Inclusive;
            case TaxInclusiveOption.NoTax:
                return QuickbookTaxInclusiveOption.NoTax;

        }

    }

    public static getTaxType(mapping: AccountingSystemMapping, taxRateId: string): string {
        if (!mapping || !taxRateId) return null;

        const tr = Enumerable
            .from(mapping.incomeTaxRates)
            .concat(mapping.expenseTaxRates)
            .where(x => x.taxRateId === taxRateId)
            .select(x => x.integrationId ? x.integrationId._id : null)
            .firstOrDefault();

        return tr;
    }

    public static getTaxTypeMapping(mapping: AccountingSystemMapping, taxRateId: string): TaxRateMapping {
        if (!mapping || !taxRateId) return null;

        const tr = Enumerable
            .from(mapping.incomeTaxRates)
            .concat(mapping.expenseTaxRates)
            .where(x => x.taxRateId === taxRateId)
            .firstOrDefault();

        return tr;
    }

    public static getCustomer(mapping: AccountingSystemMapping, customerId: string): string {
        if (!mapping) return null;

        const customer = Enumerable.from(mapping.customers).firstOrDefault(x => x.customerId === customerId);

        return customer
            ? customer?.contactId
            : null;

    }

    public static getSubcontractor(mapping: AccountingSystemMapping, technicianId: string): string {
        if (!mapping) return null;

        const technician = Enumerable.from(mapping.technicians).firstOrDefault(x => x.technicianId === technicianId);

        return technician
            ? technician?.contactId
            : null;

    }

    public static getSupplier(mapping: AccountingSystemMapping, supplierId: string): string {
        if (!mapping) return null;

        const supplier = Enumerable.from(mapping.suppliers).firstOrDefault(x => x.supplierId === supplierId);

        return supplier
            ? supplier?.contactId
            : null;

    }

    public static getContact(mapping: AccountingSystemMapping, contact: InvoiceContact, isBill: boolean) {
        let contactId: string;
        if (isBill) {
            contactId = contact.type === 'Supplier'
                ? AccountingSystemMappingUtility.getSupplier(mapping, contact._id)
                : AccountingSystemMappingUtility.getSubcontractor(mapping, contact._id)
        } else {
            contactId = AccountingSystemMappingUtility.getCustomer(mapping, contact._id);
        }
        return contactId
    }

    public static updateExistingMapping(mapping: AccountingSystemMapping, newMap: TechnicianMapping | SupplierMapping | CustomerMapping, type: InvoiceContactType): TechnicianMapping[] | SupplierMapping[] | CustomerMapping[] {

        if (!mapping || !newMap) {
            return [];
        }

        switch (type) {
            case 'Customer': {
                const customer = newMap as CustomerMapping;
                mapping.customers = this.upsertMapping(mapping.customers, customer, x => x.customerId);
                return mapping.customers;
            }

            case 'Supplier': {
                const supplier = newMap as SupplierMapping;
                mapping.suppliers = this.upsertMapping(mapping.suppliers, supplier, x => x.supplierId);
                return mapping.suppliers;
            }

            case 'Technician': {
                const technician = newMap as TechnicianMapping;
                mapping.technicians = this.upsertMapping(mapping.technicians, technician, x => x.technicianId);
                return mapping.technicians;
            }
        }
    }

    private static upsertMapping<T>(entries: T[], newMap: T, keyOf: (entry: T) => string): T[] {

        const list = entries ?? [];
        const key = keyOf(newMap);

        // An entry with no id cannot be matched - appending it keeps the caller's data without collapsing every other id-less row.
        if (!key) return [...list, newMap];

        const result: T[] = [];
        let replaced = false;

        for (const entry of list) {

            if (keyOf(entry) !== key) {
                result.push(entry);
                continue;
            }

            if (replaced) continue;

            result.push(newMap);
            replaced = true;
        }

        if (!replaced) result.push(newMap);

        return result;
    }

    private static getNotMapped(): Entity {
        return {
            _id: null,
            name: 'Not Mapped'
        } as Entity;
    }

}
